import React from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  downloadAppealAttachment,
  downloadObjectUrl,
  getPreviewObjectUrl,
  isRasterImageName,
} from '../../utils/attachmentPreview';

const THUMB_SIZE = 96;

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const useInViewOnce = (rootMargin = '160px') => {
  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node || inView) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView];
};

const downloadButtonSx = {
  p: 0.25,
  bgcolor: 'rgba(0,0,0,0.55)',
  color: '#fff',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
};

const ThumbDownloadButton = ({ onClick }) => (
  <IconButton
    size="small"
    onClick={(event) => {
      event.stopPropagation();
      onClick(event);
    }}
    title="Скачать"
    sx={{
      position: 'absolute',
      right: 4,
      bottom: 4,
      ...downloadButtonSx,
    }}
  >
    <DownloadIcon sx={{ fontSize: 16 }} />
  </IconButton>
);

const FileAttachmentRow = ({ attachment, ticketId }) => {
  const handleDownload = async (event) => {
    event.stopPropagation();
    try {
      await downloadAppealAttachment(ticketId, attachment);
    } catch {}
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.75,
        bgcolor: 'rgba(255,255,255,0.6)',
        borderRadius: 1,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
      }}
    >
      <AttachIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}
        >
          {attachment.FileName}
        </Typography>
      </Box>
      {attachment.SizeOfFile > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: '0.7rem' }}>
          {formatFileSize(attachment.SizeOfFile)}
        </Typography>
      )}
      <IconButton size="small" onClick={handleDownload} title="Скачать" sx={{ p: 0.25 }}>
        <DownloadIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

const ImageAttachmentThumb = ({ attachment, ticketId, onOpen }) => {
  const [ref, inView] = useInViewOnce();
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!inView || previewUrl || failed) return undefined;

    let cancelled = false;
    setLoading(true);
    getPreviewObjectUrl(ticketId, {
      AttachmentId: attachment.AttachmentId,
      FileName: attachment.FileName,
    })
      .then((url) => {
        if (!cancelled) {
          setPreviewUrl(url);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attachment.AttachmentId, attachment.FileName, failed, inView, previewUrl, ticketId]);

  const fileName = attachment.FileName || 'Изображение';

  const handleDownload = async (event) => {
    event.stopPropagation();
    try {
      if (previewUrl) {
        downloadObjectUrl(previewUrl, fileName);
        return;
      }
      await downloadAppealAttachment(ticketId, attachment);
    } catch {}
  };

  if (failed) {
    return (
      <Box sx={{ width: THUMB_SIZE, height: THUMB_SIZE, flexShrink: 0, position: 'relative' }}>
        <Tooltip title={fileName}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.200',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AttachIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
          </Box>
        </Tooltip>
        <ThumbDownloadButton onClick={handleDownload} />
      </Box>
    );
  }

  return (
    <Box ref={ref} sx={{ width: THUMB_SIZE, height: THUMB_SIZE, flexShrink: 0, position: 'relative' }}>
      <Tooltip title={fileName}>
        <Box
          onClick={() => previewUrl && onOpen({ url: previewUrl, attachment })}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'grey.200',
            cursor: previewUrl ? 'zoom-in' : 'default',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt={fileName}
              draggable={false}
              decoding="async"
              onError={() => setFailed(true)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              {loading ? (
                <CircularProgress size={22} />
              ) : (
                <AttachIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
              )}
            </Box>
          )}
        </Box>
      </Tooltip>
      <ThumbDownloadButton onClick={handleDownload} />
    </Box>
  );
};

const ImageLightbox = ({ preview, onClose }) => {
  if (!preview) return null;

  const fileName = preview.attachment.FileName || 'Изображение';

  const handleDownload = () => {
    downloadObjectUrl(preview.url, fileName);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: '#111',
          m: 2,
          maxWidth: '90vw',
          maxHeight: '90vh',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
        <Typography
          variant="body2"
          sx={{ color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {fileName}
        </Typography>
        <IconButton size="small" onClick={handleDownload} title="Скачать" sx={{ color: '#fff' }}>
          <DownloadIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onClose} title="Закрыть" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        component="img"
        src={preview.url}
        alt={fileName}
        sx={{
          display: 'block',
          maxWidth: '90vw',
          maxHeight: '80vh',
          objectFit: 'contain',
        }}
      />
    </Dialog>
  );
};

export const LocalFileThumb = ({ file, onRemove }) => {
  const previewUrl = React.useMemo(() => URL.createObjectURL(file), [file]);

  React.useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

  return (
    <Tooltip title={file.name}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={previewUrl}
          alt={file.name}
          draggable={false}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <IconButton
          size="small"
          onClick={onRemove}
          title="Удалить"
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            p: 0.15,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const AppealAttachments = ({ attachments, ticketId }) => {
  const [preview, setPreview] = React.useState(null);

  const images = [];
  const files = [];
  (attachments || []).forEach((attachment) => {
    if (isRasterImageName(attachment.FileName)) {
      images.push(attachment);
    } else {
      files.push(attachment);
    }
  });

  if (images.length === 0 && files.length === 0) return null;

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {images.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {images.map((attachment) => (
            <ImageAttachmentThumb
              key={attachment.AttachmentId}
              attachment={attachment}
              ticketId={ticketId}
              onOpen={setPreview}
            />
          ))}
        </Box>
      )}
      {files.map((attachment) => (
        <FileAttachmentRow
          key={attachment.AttachmentId}
          attachment={attachment}
          ticketId={ticketId}
        />
      ))}
      <ImageLightbox preview={preview} onClose={() => setPreview(null)} />
    </Box>
  );
};

export default AppealAttachments;
